import React, { useCallback, useEffect, useState } from 'react';
import { Orientation, ShipLocation, Team } from '../types';
import { Grid, ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { BattleshipGenerator } from '../battleship-generator';
import { BoardSquare } from './board-square';
import countBy from 'lodash/countBy';
import invertColor, { HexColor } from 'utils/invert-color';

interface Props {
    columns: number;
    rows: number;
    teamShipCount: number[];
    teams: Team[];
    onShipLocationsChange: (shipLocations: Team[]) => void;
}

interface BoardSquareData {
    color?: string;
    teamName?: string;
}

export const ShipManualSetup = ({ columns, rows, teamShipCount, teams, onShipLocationsChange }: Props) => {
    const getAllTeamShipCoordinates = (teams: Team[]): { x: number; y: number; teamName: string; teamColor: string, ship: ShipLocation }[] => {
        const allTeamShipCoordinates: { x: number; y: number; teamName: string; teamColor: string, ship: ShipLocation }[] = [];
        teams.forEach((t) => {
            t.shipLocations.forEach((s) => {
                const shipCoordinates = BattleshipGenerator.getShipCoordinates(s);
                shipCoordinates.forEach((c) => {
                    allTeamShipCoordinates.push({ x: c.x, y: c.y, teamName: t.name, teamColor: t.color!, ship: s });
                });
            });
        });
        return allTeamShipCoordinates;
    };

    const createBoard = useCallback((columns: number, rows: number, teams: Team[]): BoardSquareData[][] => {
        const occupiedSquares = getAllTeamShipCoordinates(teams);
        const board: BoardSquareData[][] = [];
        for (let i = 0; i < rows; i++) {
            const row: BoardSquareData[] = [];
            for (let j = 0; j < columns; j++) {
                const occupiedSquare = occupiedSquares.find((s) => s.x === j && s.y === i) ?? null;
                const boardSquareData: BoardSquareData = {
                    color: occupiedSquare?.teamColor,
                    teamName: occupiedSquare?.teamName
                };
                row.push(boardSquareData);
            }
            board.push(row);
        }
        return board;
    }, []);

    const [board, setBoard] = useState<BoardSquareData[][]>(createBoard(columns, rows, teams));
    const [shipSize, setShipSize] = useState(1);
    const [orientation, setOrientation] = useState<'horizontal' | 'vertical'>('horizontal');
    const [highlightedSquares, setHighlightedSquares] = useState<{ row: number; col: number }[]>([]);
    const [selectedTeam, setSelectedTeam] = useState<Team | undefined>(teams ? teams[0] : undefined);

    useEffect(() => {
        setBoard(createBoard(columns, rows, teams));
        if(!teams) {
            setSelectedTeam(undefined);
            return;
        }

        if(!selectedTeam) {
            setSelectedTeam(teams[0]);
            return;
        }

        setSelectedTeam(teams.find((t) => t.name === selectedTeam.name));
    }, [columns, createBoard, rows, selectedTeam, teams]);

    const placeShip = (row: number, col: number) => {
        if (board[row][col].teamName) {
            if (selectedTeam!.name !== board[row][col].teamName) {
                // existing ship on this square doesn't belong to the currently selected team -> do nothing
                return;
            }
            // existing ship on this square belongs to the currently selected team -> remove it
            const occupiedSquares = getAllTeamShipCoordinates(teams);
            const shipToRemove = occupiedSquares.find((s) => s.x === col && s.y === row && s.teamName === selectedTeam!.name)!.ship;
            onShipLocationsChange(
                teams.map((t) => ({
                    ...t,
                    shipLocations: t.shipLocations.filter((s) => s.startCoordinate.x !== shipToRemove.startCoordinate.x || s.startCoordinate.y !== shipToRemove.startCoordinate.y)
                }))
            );
            return;
        }

        const potentialShipCooordinates = BattleshipGenerator.getShipCoordinates({
            size: shipSize,
            startCoordinate: { x: col, y: row },
            orientation: orientation === 'horizontal' ? Orientation.HORIZONTAL : Orientation.VERTICAL
        });
        if (potentialShipCooordinates.some((c) => c.x >= columns || c.y >= rows) 
            || potentialShipCooordinates.some((c) => board[c.y][c.x].teamName)) {
            // potential location outside the board OR position conflict with another ship -> do nothing
            return;
        }

        if(getRemainingShipsToPlaceBySize(teamShipCount, shipSize, selectedTeam!) === 0) {
            // no ships of this size left to place -> do nothing
            return;
        }

        // square is free -> place ship
        const newTeams = teams.map((t) => {
            if (t.name !== selectedTeam!.name) {
                return t;
            }
            return {
                ...t,
                shipLocations: [
                    ...t.shipLocations,
                    {
                        size: shipSize,
                        startCoordinate: { x: col, y: row },
                        orientation: orientation === 'horizontal' ? Orientation.HORIZONTAL : Orientation.VERTICAL
                    }
                ]
            };
        });
        onShipLocationsChange(newTeams);
    };

    const handleOrientationChange = (event: React.MouseEvent<HTMLElement, MouseEvent>, newOrientation: 'horizontal' | 'vertical') => {
        if (newOrientation !== null) {
            setOrientation(newOrientation);
        }
    };

    const handleMouseEnter = (row: number, col: number) => {
        const newHighlighted = [];
        for (let i = 0; i < shipSize; i++) {
            if (orientation === 'horizontal') {
                newHighlighted.push({ row, col: col + i });
            } else {
                newHighlighted.push({ row: row + i, col });
            }
        }
        setHighlightedSquares(newHighlighted);
    };

    const onTeamChange = (event: React.MouseEvent<HTMLElement, MouseEvent>, value: string) => {
        if(value) {
            setSelectedTeam(teams.find((t) => t.name === value));
        }
    };

    const getRemainingShipsToPlaceBySize = (teamShipCount: number[], size: number, team: Team): number => {
        const setShips = countBy(team.shipLocations, (s) => s.size)[size] ?? 0;
        return teamShipCount[size - 1] - setShips;
    };

    if (selectedTeam === undefined) {
        return <Box>No teams</Box>;
    }

    return (
        <>
            <ToggleButtonGroup color="primary" value={selectedTeam.name} exclusive onChange={onTeamChange} aria-label="Teams">
                {teams.map((t) => (
                    <ToggleButton sx={{backgroundColor: t.color, '&.Mui-selected': {
                        border: `3px solid ${invertColor(t.color as HexColor)} !Important`,
                        backgroundColor: t.color
                      }}} value={t.name}>{t.name}</ToggleButton>
                ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup color="primary" value={shipSize} aria-label="Ships">
                {[1, 2, 3, 4, 5].map((size) => (
                    <ToggleButton
                        onClick={() => setShipSize(size)}
                        value = {size}
                        disabled={getRemainingShipsToPlaceBySize(teamShipCount, size, selectedTeam!) === 0}
                    >
                        {`${BattleshipGenerator.getShipName(size)} (${getRemainingShipsToPlaceBySize(teamShipCount, size, selectedTeam!)})`}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup value={orientation} exclusive onChange={handleOrientationChange} aria-label="text alignment">
                <ToggleButton value="horizontal" aria-label="left aligned">
                    Horizontal
                </ToggleButton>
                <ToggleButton value="vertical" aria-label="centered">
                    Vertical
                </ToggleButton>
            </ToggleButtonGroup>

            <Grid container>
                {board.map((row, rowIndex) => (
                    <Grid item xs={12} key={rowIndex}>
                        <Grid container justifyContent="center">
                            {row.map((square, colIndex: number) => (
                                <BoardSquare
                                    col={colIndex}
                                    row={rowIndex}
                                    key={`${rowIndex}-${colIndex}`}
                                    teamColor={square.color}
                                    isOccupied={!!square.teamName}
                                    isHighlighted={highlightedSquares.some((square) => square.row === rowIndex && square.col === colIndex)}
                                    onMouseEnter={handleMouseEnter}
                                    onClick={() => placeShip(rowIndex, colIndex)}
                                />
                            ))}
                        </Grid>
                    </Grid>
                ))}
            </Grid>
        </>
    );
};
